/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- 창 틀의 마우스 핸들러는 앞으로 올리기 보조다. 키보드는 onFocusCapture 와 창 버튼이 맡는다 */
import {
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Rnd, type DraggableData } from 'react-rnd';
import * as s from './DesktopWindow.css';
import { resolveInitialBounds } from './resolveInitialBounds';
import { readWindowPlaneSize } from './readWindowPlaneSize';
import { markWindowMaximized } from './markWindowMaximized';
import { releaseFocusToMain } from './releaseFocusToMain';
import { claimFocusOnRaise } from './claimFocusOnRaise';
import { handleWindowShortcut } from './handleWindowShortcut';
import { detectSnapZone, contentBoxSize } from './snapGeometry';
import { useParentResizeClamp } from './useParentResizeClamp';
import { useRestoreOnUnhide } from './useRestoreOnUnhide';
import { useMountPhase } from './useMountPhase';
import { WindowTitleBar } from './WindowTitleBar';
import { resolveSnapBounds, type SnapZone } from '@/shared/lib/resolveSnapBounds';
import { WindowSkeleton } from '../../atoms';
import type { WindowChromeVariant } from '@/shared/config';

export interface DesktopWindowProps {
  title: string;
  /** 창 틀의 data-window-id — 창 순환 키(Ctrl+`)가 포커스를 옮길 틀을 찾는다. */
  windowId?: string;
  children: ReactNode;
  defaultX?: number;
  defaultY?: number;
  defaultWidth?: number;
  defaultHeight?: number;
  minWidth?: number;
  minHeight?: number;
  focused?: boolean;
  zIndex?: number;
  hidden?: boolean;
  suspenseFallback?: ReactNode;
  /** TitleBar 영역 title text 대체 ReactNode. 미주입 시 기본 title string 렌더. */
  titleSlot?: ReactNode;
  /** windowContent 아래 footer band ReactNode. 미주입 시 footer band 자체 렌더 안 함. */
  footerSlot?: ReactNode;
  /** Chrome variant. 도메인 모티프 적용 (frame border/scan-line/titleBar 톤 분기). */
  variant?: WindowChromeVariant;
  onFocus?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  onPositionChange?: (pos: { x: number; y: number; width: number; height: number }) => void;
  resetRef?: React.MutableRefObject<(() => void) | null>;
}

export function DesktopWindow({
  title,
  windowId,
  children,
  defaultX,
  defaultY,
  defaultWidth = 800,
  defaultHeight = 600,
  minWidth = 320,
  minHeight = 240,
  focused = true,
  zIndex = 10,
  hidden = false,
  onFocus,
  onClose,
  suspenseFallback,
  onMinimize,
  onPositionChange,
  resetRef,
  titleSlot,
  footerSlot,
  variant = 'default',
}: DesktopWindowProps) {
  // 초기 bounds 를 부모 가용 영역으로 clamp — 좁은 뷰포트에서 윈도우가 부모를 넘쳐
  // bounds='parent' 가 드래그를 잠그는 회귀(#78) 차단. 측정 기준은 실제 Rnd 부모
  // #window-plane(폴백 #main-content) — 기준 박스가 다르면 min 이 부모보다 48px 커져
  // 리사이즈 한 번에 잠금이 재발한다.
  const initialBounds = resolveInitialBounds(
    { x: defaultX, y: defaultY, width: defaultWidth, height: defaultHeight },
    readWindowPlaneSize(),
    { width: minWidth, height: minHeight }
  );
  const resolvedX = initialBounds.x;
  const resolvedY = initialBounds.y;
  const resolvedWidth = initialBounds.width;
  const resolvedHeight = initialBounds.height;
  const resolvedMinWidth = initialBounds.minWidth;
  const resolvedMinHeight = initialBounds.minHeight;
  const rndRef = useRef<Rnd>(null);

  const mountPhase = useMountPhase(hidden);
  const [maximized, setMaximized] = useState(false);
  const [closing, setClosing] = useState(false);
  const [snapPreview, setSnapPreview] = useState<{
    zone: SnapZone;
    rect: { width: number; height: number };
  } | null>(null);

  const snappedRef = useRef(false);
  // 마지막으로 적용된 snap zone — snapped 상태에서 부모가 리사이즈되면 새 부모 기준으로
  // 같은 zone geometry 를 재적용(K2)하기 위해 보관. snappedRef 가 false 면 무의미한 값.
  const lastSnapZoneRef = useRef<SnapZone>(null);
  // maximized 상태를 ref로 미러 — ResizeObserver re-clamp가 매 toggle마다 재구독 없이 읽음
  const maximizedRef = useRef(maximized);
  useEffect(() => {
    maximizedRef.current = maximized;
  }, [maximized]);
  // 최대화한 채 보이는 동안 상태 카드를 접게 한다 — 최소화하면 가리는 창이 없으니 푼다.
  useEffect(() => {
    if (!maximized || hidden) return;
    return markWindowMaximized();
  }, [maximized, hidden]);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const parentSizeRef = useRef<{ width: number; height: number } | null>(null);
  const prevBounds = useRef({
    x: resolvedX,
    y: resolvedY,
    width: resolvedWidth,
    height: resolvedHeight,
  });

  useImperativeHandle(
    resetRef,
    () => () => {
      const rnd = rndRef.current;
      if (!rnd) return;
      // 렌더 시점 resolved 값 대신 호출 시점에 실제 Rnd 부모를 재측정해 재clamp —
      // 렌더 시점 측정(#main-content 폴백 포함)이 stale/inflated 면 리셋이 오버사이즈
      // bounds 를 재주입해 드래그 잠금을 만들던 회귀 차단.
      const parent = rnd.getSelfElement()?.parentElement;
      const parentSize = parent ? contentBoxSize(parent) : readWindowPlaneSize();
      const next = resolveInitialBounds(
        { x: defaultX, y: defaultY, width: defaultWidth, height: defaultHeight },
        parentSize,
        { width: minWidth, height: minHeight }
      );
      rnd.updatePosition({ x: next.x, y: next.y });
      rnd.updateSize({ width: next.width, height: next.height });
      setMaximized(false);

      snappedRef.current = false;
    },
    [defaultX, defaultY, defaultWidth, defaultHeight, minWidth, minHeight]
  );

  const getRelativePosition = useCallback((el: HTMLElement) => {
    const parent = el.parentElement;
    if (!parent) return { x: 0, y: 0 };
    const selfRect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    return {
      x: selfRect.left - parentRect.left,
      y: selfRect.top - parentRect.top,
    };
  }, []);

  const getParentRect = useCallback(() => {
    const self = rndRef.current?.getSelfElement();
    const parent = self?.parentElement;
    if (parent) {
      const size = contentBoxSize(parent);
      if (size.width > 0 || size.height > 0) return size;
    }
    const fallback = readWindowPlaneSize();
    if (fallback.width > 0 || fallback.height > 0) return fallback;
    return null;
  }, []);

  const handleMouseDown = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  const handleMaximize = useCallback(() => {
    const rnd = rndRef.current;
    if (!rnd) return;
    if (maximized) {
      // maximize 동안 부모가 줄었을 수 있다 — prevBounds 를 현재 부모 기준으로
      // resolveInitialBounds 에 통과시킨 뒤 적용해 복원이 부모를 넘치지 않게 한다.
      const rect = getParentRect();
      const restored = resolveInitialBounds(prevBounds.current, rect ?? { width: 0, height: 0 }, {
        width: minWidth,
        height: minHeight,
      });
      rnd.updatePosition({ x: restored.x, y: restored.y });
      rnd.updateSize({ width: restored.width, height: restored.height });
      setMaximized(false);
      snappedRef.current = false;
    } else {
      const rect = getParentRect();
      const self = rnd.getSelfElement();
      if (self && rect) {
        const pos = getRelativePosition(self);
        prevBounds.current = {
          x: pos.x,
          y: pos.y,
          width: self.offsetWidth,
          height: self.offsetHeight,
        };
        rnd.updatePosition({ x: 0, y: 0 });
        rnd.updateSize({ width: rect.width, height: rect.height });
      }
      setMaximized(true);
    }
  }, [maximized, getParentRect, getRelativePosition, minWidth, minHeight]);

  const frameRef = useRef<HTMLDivElement>(null);

  // 뒤에 있던 창이 앞으로 오거나 독 등에서 새로 열리면 포커스를 넘긴다.
  // 포커스가 없는 상태의 첫 렌더(새로고침 때 복원된 창)는 가져가지 않는다
  const wasFocusedRef = useRef<boolean | null>(null);
  useEffect(() => {
    const restoredOnLoad =
      wasFocusedRef.current === null && document.activeElement === document.body;
    if (focused && !wasFocusedRef.current && !restoredOnLoad) claimFocusOnRaise(frameRef.current);
    wasFocusedRef.current = focused;
  }, [focused]);

  const handleMinimize = useCallback(() => {
    // 키보드로 최소화 시 버튼이 display:none 되어 포커스가 body 로 추락하는 것 방지 —
    // 프레임이 숨겨지기 전 윈도우 평면으로 포커스 이양.
    releaseFocusToMain(frameRef.current);
    onMinimize?.();
  }, [onMinimize]);

  const dragRafId = useRef(0);
  const handleDrag = useCallback(
    (_e: unknown, data: DraggableData) => {
      if (dragRafId.current) return;
      dragRafId.current = requestAnimationFrame(() => {
        dragRafId.current = 0;
        const rect = parentSizeRef.current ?? getParentRect();
        if (!rect) return;
        const self = rndRef.current?.getSelfElement();
        const w = self?.offsetWidth ?? 0;
        const h = self?.offsetHeight ?? 0;
        setSnapPreview({
          zone: detectSnapZone(data.x, data.y, w, h, rect.width, rect.height),
          rect,
        });
      });
    },
    [getParentRect]
  );

  const reportPosition = useCallback(() => {
    const self = rndRef.current?.getSelfElement();
    if (self && onPositionChange) {
      const pos = getRelativePosition(self);
      onPositionChange({
        x: pos.x,
        y: pos.y,
        width: self.offsetWidth,
        height: self.offsetHeight,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps 의도 누락 (stable refs / 의도된 stale closure)
  }, [onPositionChange]);

  const handleDragStart = useCallback((_e: unknown, data: DraggableData) => {
    frameRef.current?.setAttribute('data-dragging', 'true');
    dragStartPos.current = { x: data.x, y: data.y };
    if (snappedRef.current) {
      snappedRef.current = false;
      lastSnapZoneRef.current = null;
    }
  }, []);

  const handleDragStop = useCallback(
    (_e: unknown, data: DraggableData) => {
      if (dragRafId.current) {
        cancelAnimationFrame(dragRafId.current);
        dragRafId.current = 0;
      }
      frameRef.current?.removeAttribute('data-dragging');
      setSnapPreview(null);
      const rnd = rndRef.current;
      const rect = getParentRect();
      if (!rnd || !rect) return;

      const dx = Math.abs(data.x - dragStartPos.current.x);
      const dy = Math.abs(data.y - dragStartPos.current.y);
      const didDrag = dx > 5 || dy > 5;

      const el = rnd.getSelfElement();
      const w = el?.offsetWidth ?? 0;
      const h = el?.offsetHeight ?? 0;
      const zone = didDrag ? detectSnapZone(data.x, data.y, w, h, rect.width, rect.height) : null;
      if (!zone) {
        reportPosition();
        return;
      }

      const bounds = resolveSnapBounds(zone, rect, { width: minWidth, height: minHeight });
      if (!bounds) return;

      const self = rnd.getSelfElement();
      if (self && !snappedRef.current) {
        const pos = getRelativePosition(self);
        prevBounds.current = {
          x: pos.x,
          y: pos.y,
          width: self.offsetWidth,
          height: self.offsetHeight,
        };
      }

      // 전체화면(top) 스냅 시 parent padding 상쇄
      if (zone === 'top') {
        const parent = rnd.getSelfElement()?.parentElement;
        if (parent) {
          const cs = getComputedStyle(parent);
          const pl = parseFloat(cs.paddingLeft) || 0;
          const pt = parseFloat(cs.paddingTop) || 0;
          rnd.updatePosition({ x: -pl, y: -pt });
          rnd.updateSize({ width: parent.clientWidth, height: parent.clientHeight });
        }
      } else {
        rnd.updatePosition({ x: bounds.x, y: bounds.y });
        rnd.updateSize({ width: bounds.width, height: bounds.height });
      }

      snappedRef.current = true;
      lastSnapZoneRef.current = zone;

      if (zone === 'top') setMaximized(true);
      reportPosition();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps 의도 누락 (stable refs / 의도된 stale closure)
    [getParentRect, reportPosition, minWidth, minHeight]
  );

  const handleResizeStop = useCallback(() => {
    if (snappedRef.current) {
      snappedRef.current = false;
      lastSnapZoneRef.current = null;
    }
    reportPosition();
  }, [reportPosition]);

  useParentResizeClamp({
    rndRef,
    parentSizeRef,
    maximizedRef,
    snappedRef,
    lastSnapZoneRef,
    getRelativePosition,
    minWidth,
    minHeight,
  });

  useRestoreOnUnhide({
    hidden,
    rndRef,
    maximizedRef,
    snappedRef,
    lastSnapZoneRef,
    getParentRect,
    getRelativePosition,
    minWidth,
    minHeight,
  });

  const handleClose = useCallback(() => {
    if (closing) return;
    // 180ms(windowClosing 애니메이션 길이) 후 unmount 되며 닫기 버튼이 사라지면 포커스가 body 로
    // 추락 — 닫힘 진입 즉시(프레임이 아직 살아있을 때) 윈도우 평면으로 포커스 이양.
    releaseFocusToMain(frameRef.current);
    setClosing(true);
    setTimeout(() => onClose?.(), 180);
  }, [closing, onClose]);

  const mountAnimClass =
    mountPhase === 'opening'
      ? ` ${s.windowOpening}`
      : mountPhase === 'minimizing'
        ? ` ${s.windowMinimizing}`
        : mountPhase === 'restoring'
          ? ` ${s.windowRestoring}`
          : '';
  const variantClass =
    variant === 'dailySealedLog'
      ? ` ${s.windowFrameDailySealedLog}`
      : variant === 'operatorConsole'
        ? ` ${s.windowFrameOperatorConsole}`
        : '';
  const frameClass = `${s.windowFrame}${variantClass}${focused ? ` ${s.windowFocused}` : ` ${s.windowUnfocused}`}${maximized ? ` ${s.windowMaximized}` : ''}${closing ? ` ${s.windowClosing}` : ''}${mountAnimClass}`;
  const titleClass = `${s.titleText}${focused ? '' : ` ${s.titleTextUnfocused}`}`;
  // 프리뷰는 실제 적용될 bounds 와 같은 함수로 계산한다 — 타일 기하만 그리면 min 이
  // 걸리는 창에서 프리뷰가 거짓말을 하고, 놓는 순간 창이 프리뷰 밖으로 커진다.
  const previewBounds = snapPreview
    ? resolveSnapBounds(snapPreview.zone, snapPreview.rect, {
        width: minWidth,
        height: minHeight,
      })
    : null;

  return (
    <>
      {previewBounds && (
        <div
          className={s.snapPreview}
          style={{
            left: previewBounds.x,
            top: previewBounds.y,
            width: previewBounds.width,
            height: previewBounds.height,
            zIndex: zIndex + 1,
          }}
        />
      )}
      <Rnd
        ref={rndRef}
        default={{
          x: resolvedX,
          y: resolvedY,
          width: resolvedWidth,
          height: resolvedHeight,
        }}
        minWidth={resolvedMinWidth}
        minHeight={resolvedMinHeight}
        bounds={maximized ? undefined : 'parent'}
        dragHandleClassName="desktop-window-titlebar"
        onMouseDown={handleMouseDown}
        onFocusCapture={handleMouseDown}
        // react-rnd 가 리사이즈 핸들 mousedown 에서 stopPropagation 을 걸어 onMouseDown 이 안 닿는다 —
        // 비활성 창의 모서리를 끌어도 앞으로 오게 리사이즈 시작에서 따로 포커스한다.
        onResizeStart={handleMouseDown}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        disableDragging={maximized}
        enableResizing={
          maximized
            ? false
            : {
                top: true,
                right: true,
                bottom: true,
                left: true,
                topRight: true,
                bottomRight: true,
                bottomLeft: true,
                topLeft: true,
              }
        }
        style={{
          display: mountPhase === 'minimized' ? 'none' : 'flex',
          flexDirection: 'column' as const,
          zIndex,
        }}
      >
        <div
          ref={frameRef}
          className={frameClass}
          onMouseDown={handleMouseDown}
          data-window-frame
          data-window-id={windowId}
          onKeyDown={(e) => handleWindowShortcut(e.nativeEvent, handleClose, handleMinimize)}
          role="group"
          aria-label={title}
          tabIndex={-1}
        >
          <WindowTitleBar
            title={titleSlot ?? title}
            titleClass={titleClass}
            maximized={maximized}
            onMaximize={handleMaximize}
            onMinimize={handleMinimize}
            onClose={handleClose}
          />
          <div className={s.windowContent}>
            <Suspense fallback={suspenseFallback ?? <WindowSkeleton />}>{children}</Suspense>
          </div>
          {footerSlot && <div className={s.windowFooter}>{footerSlot}</div>}
        </div>
      </Rnd>
    </>
  );
}
